// STEP MINI-CONSOLE-STUDIO-UX-1 · MiniOrderPanel 분해 (옵션 1).
//
// 역할: 기존 통합 패널을 2개의 재사용 가능한 섹션으로 분리.
//   · SelfLivePanel        — 자사 LIVE (streaming_session_id) 미니주문 (compact 사이드용).
//   · ExternalMiniSection — 외부 SNS 전용 미니주문 운영 (메인 영역 승격용).
//   · MiniOrderPanel      — 기존 default export (하위호환 · 두 섹션을 세로로 결합).
//
// 변경 금지: Backend API, ConsumerMini, canonical create_order, OMS/WMS, Overlay.
// 중복 구현 금지 — 생성/상태 전이/링크 생성 로직은 ExternalMiniSection 안에 유지.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Link as LinkIcon, QrCode, Copy, ClipboardList, ExternalLink,
  Plus, Play, Square, RotateCcw, Loader2, Check, X as XIcon,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import api from '@/lib/api';

const PLATFORMS = [
  { k: 'tiktok',    label: 'TikTok' },
  { k: 'instagram', label: 'Instagram' },
  { k: 'youtube',   label: 'YouTube' },
  { k: 'other',     label: '기타' },
];

const STATUS_BADGE = {
  '예정':   { label: '예정', cls: 'bg-blue-100 text-blue-700' },
  '라이브': { label: '진행중', cls: 'bg-green-100 text-green-700' },
  '종료':   { label: '종료', cls: 'bg-gray-200 text-gray-600' },
};

const _copy = async (url) => {
  if (!url) return;
  try { await navigator.clipboard.writeText(url); toast.success('링크를 복사했습니다.'); }
  catch { toast.error('링크 복사에 실패했습니다.'); }
};

// ─── 자사 LIVE 미니주문 (sticky 사이드 compact) ────────────────────────
export const SelfLivePanel = ({ streamingSessionId, productsCount = 0 }) => {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [showQR, setShowQR] = useState(false);
  const miniUrl = useMemo(() => {
    if (!streamingSessionId) return '';
    try { return `${window.location.origin}/live/${streamingSessionId}/mini`; }
    catch { return ''; }
  }, [streamingSessionId]);
  useEffect(() => {
    if (!miniUrl || !showQR) return;
    let c = false;
    QRCode.toDataURL(miniUrl, { width: 240, margin: 1 })
      .then((u) => { if (!c) setQrDataUrl(u); })
      .catch(() => { if (!c) setQrDataUrl(''); });
    return () => { c = true; };
  }, [miniUrl, showQR]);
  if (!streamingSessionId) {
    return (
      <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 bg-black/[0.015] dark:bg-white/[0.02]"
           data-testid="mini-order-panel-self-live-empty">
        <p className="text-[11px] text-gray-500">
          LIVE 세션이 시작되면 자사라이브 미니주문 링크가 자동 생성됩니다.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 bg-black/[0.015] dark:bg-white/[0.02]"
         data-testid="mini-order-panel-self-live">
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold text-gray-700 dark:text-gray-300">
          <LinkIcon size={12} /> 자사 LIVE 미니주문
        </div>
        <span className="text-[10px] text-gray-500" data-testid="mini-order-panel-count">
          연결 상품 {productsCount}개
        </span>
      </div>
      <div className="flex items-stretch gap-1.5 mb-2">
        <input type="text" readOnly value={miniUrl}
               className="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-white dark:bg-black border border-black/10 dark:border-white/10 text-[11px] font-mono text-gray-700 dark:text-gray-200"
               data-testid="mini-order-panel-url"
               onFocus={(e) => e.target.select()} />
        <button type="button" onClick={() => _copy(miniUrl)}
                className="px-2.5 py-1.5 rounded-lg bg-black text-white dark:bg-white dark:text-black text-[11px] font-black inline-flex items-center gap-1"
                data-testid="mini-order-panel-copy">
          <Copy size={11} /> 복사
        </button>
      </div>
      <div className="flex items-center gap-1.5">
        <button type="button" onClick={() => setShowQR((v) => !v)}
                className="flex-1 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-[11px] font-black inline-flex items-center justify-center gap-1"
                data-testid="mini-order-panel-qr-toggle"
                aria-expanded={showQR}>
          <QrCode size={11} /> {showQR ? 'QR 숨기기' : 'QR 보기'}
        </button>
        <button type="button" onClick={() => { try { window.open(miniUrl, '_blank', 'noopener'); } catch {} }}
                className="flex-1 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-[11px] font-black inline-flex items-center justify-center gap-1"
                data-testid="mini-order-panel-preview">
          <ExternalLink size={11} /> 미리보기
        </button>
        <a href="/seller/orders" target="_blank" rel="noopener noreferrer"
           className="flex-1 py-1.5 rounded-lg bg-[#FF3B30] text-white text-[11px] font-black inline-flex items-center justify-center gap-1"
           data-testid="mini-order-panel-orders-link">
          <ClipboardList size={11} /> 주문관리
        </a>
      </div>
      {showQR && (
        <div className="mt-2 flex flex-col items-center gap-1" data-testid="mini-order-panel-qr-block">
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="자사 LIVE 미니주문 QR"
                 className="w-40 h-40 rounded-lg bg-white p-1"
                 data-testid="mini-order-panel-qr-img" />
          ) : (
            <div className="w-40 h-40 flex items-center justify-center rounded-lg bg-white/10 text-[10px] text-gray-500"
                 data-testid="mini-order-panel-qr-loading">생성 중…</div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── 외부 LIVE 생성 폼 ─────────────────────────────────────────────────
const CreateExternalForm = ({ busy, onCreate, onCancel }) => {
  const [platform, setPlatform] = useState('tiktok');
  const [title, setTitle] = useState('');
  const submit = () => {
    const t = title.trim();
    if (!t) { toast.error('방송 제목을 입력해주세요.'); return; }
    onCreate({ title: t, external_platform: platform });
  };
  return (
    <div className="rounded-xl border border-[#FF3B30]/40 bg-[#FF3B30]/[0.03] p-3"
         data-testid="mini-order-panel-create-form">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[12px] font-black text-black dark:text-white">새 미니주문 생성 (외부 LIVE)</p>
        <button type="button" onClick={onCancel} className="text-gray-500"
                data-testid="mini-order-panel-create-cancel"
                disabled={busy}><XIcon size={14} /></button>
      </div>
      <div className="mb-2">
        <p className="text-[10px] font-bold text-gray-600 mb-1">① 플랫폼 선택</p>
        <div className="grid grid-cols-4 gap-1">
          {PLATFORMS.map((p) => (
            <button key={p.k} type="button" onClick={() => setPlatform(p.k)}
                    disabled={busy}
                    className={`py-1.5 rounded-lg border text-[11px] font-black ${
                      platform === p.k
                        ? 'border-[#FF3B30] bg-[#FF3B30] text-white'
                        : 'border-black/10 dark:border-white/10 text-gray-700 dark:text-gray-300'
                    }`}
                    data-testid={`mini-order-panel-platform-${p.k}`}
                    aria-pressed={platform === p.k}>{p.label}</button>
          ))}
        </div>
      </div>
      <div className="mb-2">
        <p className="text-[10px] font-bold text-gray-600 mb-1">② 방송 제목</p>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value.slice(0, 80))}
               disabled={busy}
               placeholder="예) 오늘 신상품 라이브"
               className="w-full px-2 py-1.5 rounded-lg bg-white dark:bg-black border border-black/10 dark:border-white/10 text-[12px]"
               data-testid="mini-order-panel-create-title" />
      </div>
      <div className="flex items-center gap-1.5">
        <button type="button" onClick={onCancel}
                disabled={busy}
                className="flex-1 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-[11px] font-black"
                data-testid="mini-order-panel-create-cancel-btn">취소</button>
        <button type="button" onClick={submit}
                disabled={busy || !title.trim()}
                className="flex-1 py-1.5 rounded-lg bg-[#FF3B30] text-white text-[11px] font-black inline-flex items-center justify-center gap-1 disabled:opacity-50"
                data-testid="mini-order-panel-create-submit">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
          미니주문 생성
        </button>
      </div>
      <p className="mt-2 text-[10px] text-gray-500">
        LiveKit 송출 없이 외부 플랫폼 LIVE 중에 사용할 수 있는 간편 주문 링크를 만듭니다.
      </p>
    </div>
  );
};

// ─── 외부 LIVE 세션 1건 (리스트 row) ──────────────────────────────────-
const ExternalSessionRow = ({ item, onPatch, onToggleQR, qrOpenId, compact = false }) => {
  const sid = item.schedule_id;
  const status = item.status || '예정';
  const platform = (item.external_platform || 'other').toLowerCase();
  const platformLabel = PLATFORMS.find((p) => p.k === platform)?.label || '기타';
  const sellableAfterEnd = !!item.external_sellable_after_end;
  const miniUrl = (typeof window !== 'undefined')
    ? `${window.location.origin}/live-mini/${sid}` : '';
  const [qrDataUrl, setQrDataUrl] = useState('');
  const showQR = qrOpenId === sid;
  useEffect(() => {
    if (!showQR || !miniUrl) return;
    let c = false;
    QRCode.toDataURL(miniUrl, { width: 220, margin: 1 })
      .then((u) => { if (!c) setQrDataUrl(u); })
      .catch(() => { if (!c) setQrDataUrl(''); });
    return () => { c = true; };
  }, [showQR, miniUrl]);
  const badge = STATUS_BADGE[status] || STATUS_BADGE['예정'];
  return (
    <div className={`rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-black/40 ${compact ? 'p-2' : 'p-2.5'}`}
         data-testid={`mini-order-ext-row-${sid}`}
         data-schedule-id={sid}
         data-status={status}
         data-platform={platform}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-black ${badge.cls}`}
              data-testid={`mini-order-ext-row-status-${sid}`}>{badge.label}</span>
        <span className="text-[10px] text-gray-500">{platformLabel}</span>
        <p className="flex-1 min-w-0 text-[12px] font-black truncate"
           data-testid={`mini-order-ext-row-title-${sid}`}>{item.title}</p>
        <span className="text-[10px] text-gray-500">상품 {item.linked_product_count || 0}개</span>
      </div>
      <div className="flex items-stretch gap-1.5 mb-1.5">
        <input type="text" readOnly value={miniUrl}
               onFocus={(e) => e.target.select()}
               className="flex-1 min-w-0 px-2 py-1 rounded-md bg-black/[0.03] dark:bg-white/[0.04] text-[10px] font-mono text-gray-700 dark:text-gray-200"
               data-testid={`mini-order-ext-row-url-${sid}`} />
        <button type="button" onClick={() => _copy(miniUrl)}
                className="px-2 py-1 rounded-md bg-black text-white dark:bg-white dark:text-black text-[10px] font-black inline-flex items-center gap-1"
                data-testid={`mini-order-ext-row-copy-${sid}`}>
          <Copy size={10} /> 복사
        </button>
        {!compact && (
          <button type="button" onClick={() => onToggleQR(sid)}
                  className="px-2 py-1 rounded-md border border-black/10 dark:border-white/10 text-[10px] font-black inline-flex items-center gap-1"
                  data-testid={`mini-order-ext-row-qr-${sid}`}
                  aria-expanded={showQR}><QrCode size={10} /> QR</button>
        )}
      </div>
      {!compact && (
        <>
          <div className="flex items-center gap-1 mb-1.5" role="group" aria-label="판매 상태">
            {status !== '라이브' && (
              <button type="button" onClick={() => onPatch(sid, { status: '라이브' })}
                      className="flex-1 py-1 rounded-md bg-green-600 text-white text-[10px] font-black inline-flex items-center justify-center gap-1"
                      data-testid={`mini-order-ext-row-start-${sid}`}>
                <Play size={10} /> 판매 시작
              </button>
            )}
            {status === '라이브' && (
              <button type="button" onClick={() => onPatch(sid, { status: '종료' })}
                      className="flex-1 py-1 rounded-md bg-gray-700 text-white text-[10px] font-black inline-flex items-center justify-center gap-1"
                      data-testid={`mini-order-ext-row-end-${sid}`}>
                <Square size={10} /> 판매 종료
              </button>
            )}
            {status === '종료' && (
              <button type="button" onClick={() => onPatch(sid, { status: '예정' })}
                      className="flex-1 py-1 rounded-md bg-blue-600 text-white text-[10px] font-black inline-flex items-center justify-center gap-1"
                      data-testid={`mini-order-ext-row-reopen-${sid}`}>
                <RotateCcw size={10} /> 예정으로 재개
              </button>
            )}
            <button type="button"
                    onClick={() => onPatch(sid, { external_sellable_after_end: !sellableAfterEnd })}
                    className={`py-1 px-2 rounded-md border text-[10px] font-black ${
                      sellableAfterEnd
                        ? 'bg-black text-white border-black dark:bg-white dark:text-black'
                        : 'border-black/15 text-gray-700 dark:text-gray-300'
                    }`}
                    aria-pressed={sellableAfterEnd}
                    data-testid={`mini-order-ext-row-sellable-${sid}`}
                    title="종료 후에도 계속 판매 허용">
              {sellableAfterEnd ? <Check size={10} /> : null} 종료후 판매
            </button>
          </div>
          <div className="flex items-center gap-1">
            <a href={`/seller/live-control?sid=${encodeURIComponent(sid)}`}
               className="flex-1 py-1 rounded-md border border-black/10 dark:border-white/10 text-[10px] font-black inline-flex items-center justify-center gap-1"
               data-testid={`mini-order-ext-row-products-${sid}`}>
              <LinkIcon size={10} /> 상품 관리
            </a>
            <a href="/seller/orders" target="_blank" rel="noopener noreferrer"
               className="flex-1 py-1 rounded-md bg-[#FF3B30] text-white text-[10px] font-black inline-flex items-center justify-center gap-1"
               data-testid={`mini-order-ext-row-orders-${sid}`}>
              <ClipboardList size={10} /> 주문관리
            </a>
          </div>
          {showQR && (
            <div className="mt-2 flex flex-col items-center" data-testid={`mini-order-ext-row-qr-block-${sid}`}>
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="외부 LIVE 미니주문 QR"
                     className="w-32 h-32 rounded-lg bg-white p-1"
                     data-testid={`mini-order-ext-row-qr-img-${sid}`} />
              ) : (
                <div className="w-32 h-32 flex items-center justify-center rounded-lg bg-white/10 text-[10px] text-gray-500">생성 중…</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── 외부 LIVE 운영 섹션 (메인 승격 가능) ─────────────────────────────-
// Props:
//   · defaultCollapsed: 기본 접힘 (모바일/sm) — 사용자 탭하여 전개.
//   · variant: 'full' (default · 메인) | 'compact' (사이드 — 생성 폼 없음, 리스트 compact).
export const ExternalMiniSection = ({ defaultCollapsed = false, variant = 'full' }) => {
  const [collapsed, setCollapsed] = useState(!!defaultCollapsed);
  const [extItems, setExtItems] = useState([]);
  const [extLoading, setExtLoading] = useState(false);
  const [extCreating, setExtCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [qrOpenId, setQrOpenId] = useState(null);
  const [patchBusy, setPatchBusy] = useState({});
  const isCompact = variant === 'compact';

  const loadExt = useCallback(async () => {
    setExtLoading(true);
    try {
      const r = await api.get('/seller/mini-sessions');
      setExtItems(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch { setExtItems([]); }
    finally { setExtLoading(false); }
  }, []);
  useEffect(() => { loadExt(); }, [loadExt]);

  const toggleQR = (sid) => setQrOpenId((cur) => (cur === sid ? null : sid));
  const createExt = async ({ title, external_platform }) => {
    if (extCreating) return;
    setExtCreating(true);
    try {
      await api.post('/seller/mini-sessions', { title, external_platform });
      toast.success('미니주문이 생성되었습니다.');
      setShowCreate(false);
      await loadExt();
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (err?.response?.status === 403 ? '판매자 권한이 필요합니다.' : '미니주문 생성에 실패했습니다.');
      toast.error(msg);
    } finally { setExtCreating(false); }
  };
  const patchExt = async (sid, body) => {
    if (patchBusy[sid]) return;
    setPatchBusy((m) => ({ ...m, [sid]: true }));
    try {
      await api.patch(`/seller/mini-sessions/${sid}`, body);
      await loadExt();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '상태 변경에 실패했습니다.');
    } finally {
      setPatchBusy((m) => { const n = { ...m }; delete n[sid]; return n; });
    }
  };

  const totalCount = extItems.length;
  const liveCount = extItems.filter((x) => x.status === '라이브').length;

  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.015] dark:bg-white/[0.02]"
         data-testid="mini-order-panel-external"
         data-variant={variant}
         data-collapsed={collapsed ? '1' : '0'}>
      <button type="button"
              onClick={() => setCollapsed((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
              aria-expanded={!collapsed}
              data-testid="mini-order-ext-header-toggle">
        <LinkIcon size={13} className="text-[#FF3B30]" />
        <span className="text-[12px] font-black text-black dark:text-white">외부 LIVE 미니주문</span>
        <span className="px-1 py-[1px] rounded-full bg-black text-white dark:bg-white dark:text-black text-[9px] font-black">BETA</span>
        <span className="text-[10px] text-gray-500" data-testid="mini-order-ext-count">
          운영 중 {liveCount} / 총 {totalCount}
        </span>
        <span className="ml-auto text-gray-400">
          {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        </span>
      </button>
      {!collapsed && (
        <div className="px-3 pb-3">
          {!isCompact && (
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] text-gray-500">
                TikTok · Instagram · YouTube 등 외부 플랫폼 LIVE 중에 쓰는 간편 주문 링크입니다. LiveKit 송출 없이 사용 가능.
              </p>
              {!showCreate && (
                <button type="button" onClick={() => setShowCreate(true)}
                        className="shrink-0 px-2 py-1 rounded-md bg-[#FF3B30] text-white text-[10px] font-black inline-flex items-center gap-1"
                        data-testid="mini-order-panel-create-open">
                  <Plus size={10} /> 새 미니주문
                </button>
              )}
            </div>
          )}
          {!isCompact && showCreate && (
            <div className="mb-2">
              <CreateExternalForm busy={extCreating}
                                   onCreate={createExt}
                                   onCancel={() => setShowCreate(false)} />
            </div>
          )}
          {extLoading ? (
            <div className="text-[11px] text-gray-500 py-2 text-center"
                 data-testid="mini-order-panel-ext-loading">불러오는 중…</div>
          ) : extItems.length === 0 ? (
            <div className="text-[11px] text-gray-500 py-3 text-center border border-dashed border-black/10 dark:border-white/10 rounded-lg"
                 data-testid="mini-order-panel-ext-empty">
              아직 생성한 외부 LIVE 미니주문이 없습니다.
            </div>
          ) : (
            <div className="flex flex-col gap-1.5" data-testid="mini-order-panel-ext-list">
              {extItems.map((it) => (
                <ExternalSessionRow key={it.schedule_id} item={it}
                                     onPatch={patchExt}
                                     onToggleQR={toggleQR}
                                     qrOpenId={qrOpenId}
                                     compact={isCompact} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── 하위호환 default export — 두 섹션을 세로로 결합 ──────────────────-
const MiniOrderPanel = ({ streamingSessionId, productsCount = 0 }) => (
  <div className="flex flex-col gap-2" data-testid="mini-order-panel">
    <SelfLivePanel streamingSessionId={streamingSessionId} productsCount={productsCount} />
    <ExternalMiniSection defaultCollapsed={false} variant="full" />
  </div>
);

export default MiniOrderPanel;
